import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, MapPin, ExternalLink, Mail, Briefcase, Building2, Globe } from "lucide-react";
import { CvShell } from "@/components/cv/CvShell";
import { CvGate } from "@/components/cv/CvGate";
import { useAuth } from "@/context/AuthContext";
import { useCredits } from "@/context/CreditsContext";
import { cvApi } from "@/lib/api";

const matchColor = (s) => (s >= 60 ? "#16a34a" : s >= 30 ? "#ca8a04" : "#6b7280");

export default function CvEmployers() {
  const { user } = useAuth();
  const { confirmAndRun, priceOf } = useCredits();
  const navigate = useNavigate();
  const [cvs, setCvs] = useState([]);
  const [cvId, setCvId] = useState("");
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [hasCv, setHasCv] = useState(false);
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!user) return;
    cvApi.list().then((d) => { setCvs(d.items || []); if (d.items?.[0]) setCvId(d.items[0].id); }).catch(() => {});
  }, [user]);

  const search = async () => {
    const res = await confirmAndRun("cv_employers_search", async () => {
      setLoading(true);
      try {
        return await cvApi.employersSearch({ cv_id: cvId, q, location, remote_only: remoteOnly });
      } finally {
        setLoading(false);
      }
    });
    if (res && res.items !== undefined) {
      setItems(res.items || []);
      setHasCv(res.has_cv);
      setSource(res.source || "");
    }
  };

  const createLetter = async (job) => {
    setBusy(`letter-${job.url}`);
    try {
      const l = await cvApi.letterCreate({ cv_id: cvId, company: job.company || "", role: job.title || "", job_offer: job.snippet || "" });
      navigate(`/cv/list/${l.id}`);
    } catch {
      toast.error("Nie udało się utworzyć listu.");
      setBusy("");
    }
  };

  const saveApp = async (job) => {
    setBusy(`app-${job.url}`);
    try {
      await cvApi.appCreate({ cv_id: cvId, company: job.company || "", role: job.title || "", job_offer: job.snippet || "" });
      toast.success("Zapisano jako aplikację.");
    } catch {
      toast.error("Nie udało się zapisać aplikacji.");
    } finally {
      setBusy("");
    }
  };

  const inputCls = "w-full rounded-button border border-brand-line bg-brand-bg px-3 py-2.5 text-sm text-brand-text focus:border-brand-accent focus:outline-none";

  return (
    <CvShell active="pracodawcy">
      <div data-testid="cv-employers" className="pb-10">
        <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Pracodawcy i oferty</h1>
        <p className="mt-2 max-w-2xl text-brand-sec">Przeszukaj aktualne oferty pracy i zobacz, jak pasują do Twojego CV. Wybierz CV, aby policzyć dopasowanie.</p>

        {!user ? (
          <div className="mt-8 max-w-2xl"><CvGate returnTo="/cv/pracodawcy" message="Załóż bezpłatne konto, aby przeszukiwać oferty i zapisywać aplikacje." /></div>
        ) : (
          <>
            {/* Pasek wyszukiwania */}
            <div className="mt-6 rounded-panel border border-brand-line bg-brand-surface p-4" data-testid="cv-employers-search">
              <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr_0.9fr_auto]">
                <div className="relative">
                  <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                  <input data-testid="cv-employers-q" className={`${inputCls} pl-9`} placeholder="Stanowisko lub słowo kluczowe" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
                </div>
                <div className="relative">
                  <MapPin size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                  <input data-testid="cv-employers-location" className={`${inputCls} pl-9`} placeholder="Lokalizacja" value={location} onChange={(e) => setLocation(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
                </div>
                <select data-testid="cv-employers-cv" className={inputCls} value={cvId} onChange={(e) => setCvId(e.target.value)}>
                  <option value="">Bez dopasowania do CV</option>
                  {cvs.map((c) => (<option key={c.id} value={c.id}>{c.title}</option>))}
                </select>
                <button type="button" data-testid="cv-employers-search-btn" disabled={loading} onClick={search} className="inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Szukaj{priceOf("cv_employers_search") ? ` · ${priceOf("cv_employers_search")} kr` : ""}
                </button>
              </div>
              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-[13px] text-brand-text">
                <input type="checkbox" data-testid="cv-employers-remote" checked={remoteOnly} onChange={(e) => setRemoteOnly(e.target.checked)} className="h-4 w-4 accent-[color:var(--brand-accent,#b6f500)]" /> Tylko zdalne
              </label>
              <p className="mt-2 text-[12px] text-brand-muted">{source === "adzuna" ? "Źródło: Adzuna — oferty z rynku polskiego (agregator wielu portali)." : "Źródło: Arbeitnow (oferty z Europy, w tym zdalne). Dołączamy źródło z pełnym pokryciem rynku polskiego."}</p>
            </div>

            {items === null ? (
              <div className="mt-8 rounded-panel border border-dashed border-brand-line px-6 py-12 text-center text-brand-muted" data-testid="cv-employers-hint">
                Wpisz frazę i kliknij „Szukaj", aby zobaczyć aktualne oferty.
              </div>
            ) : loading ? (
              <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-brand-accent" /></div>
            ) : items.length === 0 ? (
              <div className="mt-8 rounded-panel border border-dashed border-brand-line px-6 py-12 text-center text-brand-muted" data-testid="cv-employers-empty">
                Brak ofert dla podanych kryteriów. Spróbuj innej frazy lub usuń filtr lokalizacji.
              </div>
            ) : (
              <ul className="mt-6 space-y-3" data-testid="cv-employers-list">
                {items.map((job, i) => (
                  <li key={`${job.url}-${i}`} data-testid="cv-employer-item" className="rounded-panel border border-brand-line bg-brand-surface p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-brand-text">{job.title}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-brand-sec">
                          <span className="inline-flex items-center gap-1"><Building2 size={13} /> {job.company || "—"}</span>
                          {job.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
                          {job.remote && <span className="inline-flex items-center gap-1 text-brand-accent"><Globe size={13} /> Zdalnie</span>}
                        </p>
                      </div>
                      {hasCv && (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white" style={{ background: matchColor(job.match) }} title="Dopasowanie do CV">{job.match}%</span>
                      )}
                    </div>
                    {job.snippet && <p className="mt-2 line-clamp-2 text-[13px] text-brand-muted">{job.snippet}</p>}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {job.url && (
                        <a href={job.url} target="_blank" rel="noreferrer" data-testid="cv-employer-open" className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-2 text-[13px] font-semibold text-brand-text hover:border-brand-accent/60">
                          <ExternalLink size={14} /> Zobacz ofertę
                        </a>
                      )}
                      <button type="button" data-testid="cv-employer-letter" disabled={busy === `letter-${job.url}`} onClick={() => createLetter(job)} className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-2 text-[13px] font-semibold text-brand-text hover:border-brand-accent/60 disabled:opacity-50">
                        {busy === `letter-${job.url}` ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Utwórz list
                      </button>
                      <button type="button" data-testid="cv-employer-save" disabled={busy === `app-${job.url}`} onClick={() => saveApp(job)} className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-2 text-[13px] font-semibold text-brand-text hover:border-brand-accent/60 disabled:opacity-50">
                        {busy === `app-${job.url}` ? <Loader2 size={14} className="animate-spin" /> : <Briefcase size={14} />} Zapisz jako aplikację
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </CvShell>
  );
}
